import {
  AttendanceRecord,
  LeaveRequest,
  AttendanceSummary,
  AttendanceFilters,
  Employee,
} from "./types";
import { IMasterConfigOption } from "../../store/apis/masterConfig.api";

export const getStatusLabel = (status: string): string => {
  switch (status) {
    case "present":
      return "P";
    case "absent":
      return "A";
    case "half_day":
      return "HD";
    case "leave":
      return "EL";
    case "sick_leave":
      return "SL";
    case "lwp":
      return "LWP";
    default:
      return status.toUpperCase();
  }
};

export const getLeaveStatusLabel = (leaveType: string): string => {
  // console.log("LEAVE TYPE FROM API:", leaveType);
  switch (leaveType) {
    case "earned":
      return "EL";
    case "sick":
      return "SL";
    case "lwp":
      return "LWP";
    case "wfh":
      return "WFH";
    case "flexi_weekend":
      return "FWL";
    case "compensatory_leave":
      return "CPL";
    case "optional_holiday":
      return "OH";
    default:
      return "EL"; // Default to earned leave
  }
};

// Check if status is a combined half-day status (e.g., "P/EL", "HD/SL")
export const isHalfDayStatus = (status: string): boolean => {
  return status.includes("/");
};

// Parse combined half-day status into parts
export const parseHalfDayStatus = (
  status: string
): { work: string; leave: string } | null => {
  if (!status.includes("/")) return null;
  const [work, leave] = status.split("/");
  return { work, leave };
};

const getHashIndex = (str: string, max: number): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash) % max;
};

const getInitialsCode = (displayName: string): string => {
  const words = displayName.trim().split(/\s+/);
  if (words.length >= 2) {
    return words.map(w => w[0]).join("").toUpperCase().slice(0, 3);
  }
  return displayName.slice(0, 3).toUpperCase();
};

export const getShortCode = (displayName: string, filterCode: string): string => {
  const fc = filterCode.toLowerCase();
  
  // Map leave type codes to status codes exactly like the backend
  const leaveTypeCodes: Record<string, string> = {
    earned: 'EL',
    earned_leave: 'EL',
    el: 'EL',
    sick: 'SL',
    sick_leave: 'SL',
    sl: 'SL',
    lwp: 'LWP',
    leave_without_pay: 'LWP',
    wfh: 'WFH',
    work_from_home: 'WFH',
    flexi_weekend: 'FWL',
    flexi_weekend_leave: 'FWL',
    fwl: 'FWL',
    compensatory: 'CPL',
    compensatory_leave: 'CPL',
    cpl: 'CPL',
    optional_holiday: 'OH',
    oh: 'OH',
  };

  if (leaveTypeCodes[fc]) {
    return leaveTypeCodes[fc];
  }

  // Initials generator for custom leave types
  return getInitialsCode(displayName);
};

export const getDisplayStatus = (
  status: string,
  leaveTypes?: IMasterConfigOption[]
): string => {
  if (!status) return "";
  const upperStatus = status.toUpperCase();

  // Handle combined half-day statuses (e.g., "P/RAI" -> "P/RL")
  if (isHalfDayStatus(status)) {
    const parts = parseHalfDayStatus(status);
    if (parts) {
      const workPart = parts.work.toUpperCase();
      const leavePart = parts.leave;
      const leaveDisplay = getDisplayStatus(leavePart, leaveTypes);
      return `${workPart}/${leaveDisplay}`;
    }
  }

  // Try to match leaveTypes from database config
  if (leaveTypes && leaveTypes.length > 0) {
    const matched = leaveTypes.find(lt => {
      const fc = lt.filterCode.toLowerCase();
      const displayName = lt.displayName;
      const shortCode = getShortCode(displayName, lt.filterCode);
      const backendSlice = fc.toUpperCase().slice(0, 3);
      const nameSlice = displayName.toUpperCase().slice(0, 3);

      const s = upperStatus;
      return (
        fc.toUpperCase() === s ||
        shortCode.toUpperCase() === s ||
        backendSlice === s ||
        nameSlice === s
      );
    });

    if (matched) {
      return getShortCode(matched.displayName, matched.filterCode);
    }
  }

  return upperStatus;
};

export interface StatusDisplayInfo {
  label: string;
  gridClass: string;
  tooltipBgClass: string;
  tooltipTextClass: string;
}

const getStandardOrCustomLeaveInfo = (code: string, label: string): StatusDisplayInfo => {
  const norm = code.toLowerCase();

  // Standard styling definitions
  const standardStyles: Record<string, { gridClass: string; tooltipBgClass: string; tooltipTextClass: string }> = {
    el: { gridClass: "bg-primary-100 text-primary-800", tooltipBgClass: "bg-primary-50", tooltipTextClass: "text-primary-600" },
    sl: { gridClass: "bg-purple-100 text-purple-800", tooltipBgClass: "bg-purple-50", tooltipTextClass: "text-purple-600" },
    lwp: { gridClass: "bg-slate-200 text-slate-800", tooltipBgClass: "bg-slate-50", tooltipTextClass: "text-slate-600" },
    wfh: { gridClass: "bg-cyan-100 text-cyan-800", tooltipBgClass: "bg-cyan-50", tooltipTextClass: "text-cyan-600" },
    fwl: { gridClass: "bg-teal-100 text-teal-800", tooltipBgClass: "bg-teal-50", tooltipTextClass: "text-teal-600" },
    cpl: { gridClass: "bg-indigo-100 text-indigo-800", tooltipBgClass: "bg-indigo-50", tooltipTextClass: "text-indigo-600" },
    oh: { gridClass: "bg-yellow-100 text-yellow-800", tooltipBgClass: "bg-yellow-50", tooltipTextClass: "text-yellow-600" },
  };

  if (standardStyles[norm]) {
    return {
      label,
      ...standardStyles[norm]
    };
  }

  // Custom styling via hashing
  const customGridPalette = [
    "bg-pink-100 text-pink-800",
    "bg-emerald-100 text-emerald-800",
    "bg-rose-100 text-rose-800",
    "bg-fuchsia-100 text-fuchsia-800",
    "bg-amber-100 text-amber-800",
    "bg-sky-100 text-sky-800",
    "bg-teal-100 text-teal-800",
    "bg-lime-100 text-lime-800",
    "bg-violet-100 text-violet-800",
    "bg-cyan-100 text-cyan-800",
    "bg-orange-100 text-orange-800",
    "bg-indigo-100 text-indigo-800",
    "bg-purple-100 text-purple-800",
    "bg-yellow-100 text-yellow-800",
    "bg-stone-100 text-stone-800"
  ];

  const customTooltipPalette = [
    { color: 'text-pink-600', bgColor: 'bg-pink-50' },
    { color: 'text-emerald-600', bgColor: 'bg-emerald-50' },
    { color: 'text-rose-600', bgColor: 'bg-rose-50' },
    { color: 'text-fuchsia-600', bgColor: 'bg-fuchsia-50' },
    { color: 'text-amber-600', bgColor: 'bg-amber-50' },
    { color: 'text-sky-600', bgColor: 'bg-sky-50' },
    { color: 'text-teal-600', bgColor: 'bg-teal-50' },
    { color: 'text-lime-600', bgColor: 'bg-lime-50' },
    { color: 'text-violet-600', bgColor: 'bg-violet-50' },
    { color: 'text-cyan-600', bgColor: 'bg-cyan-50' },
    { color: 'text-orange-600', bgColor: 'bg-orange-50' },
    { color: 'text-indigo-600', bgColor: 'bg-indigo-50' },
    { color: 'text-purple-600', bgColor: 'bg-purple-50' },
    { color: 'text-yellow-600', bgColor: 'bg-yellow-50' },
    { color: 'text-stone-600', bgColor: 'bg-stone-50' }
  ];

  const index = getHashIndex(norm, customGridPalette.length);
  const gridClass = customGridPalette[index];
  const tooltip = customTooltipPalette[index];

  return {
    label,
    gridClass,
    tooltipBgClass: tooltip.bgColor,
    tooltipTextClass: tooltip.color
  };
};

export const getStatusDisplayInfo = (
  status: string,
  leaveTypes?: IMasterConfigOption[]
): StatusDisplayInfo => {
  const lowerStatus = status.toLowerCase();

  // 1. Handle combined half-day statuses (e.g., "p/el", "hd/sl", "p/rai")
  if (isHalfDayStatus(status)) {
    const parts = parseHalfDayStatus(status);
    if (parts) {
      const leaveInfo = getStatusDisplayInfo(parts.leave, leaveTypes);
      const leaveBgClean = leaveInfo.gridClass.match(/bg-(\S+)/)?.[1] || "blue-100";
      const leaveTooltipBgClean = leaveInfo.tooltipBgClass.match(/bg-(\S+)/)?.[1] || "blue-50";

      if (parts.work.toLowerCase() === "p") {
        return {
          label: `Half Present + Half ${leaveInfo.label}`,
          gridClass: `bg-gradient-to-r from-green-100 to-${leaveBgClean} ${leaveInfo.gridClass.match(/text-\S+/)?.[0] || "text-green-800"}`,
          tooltipBgClass: `bg-gradient-to-r from-green-50 to-${leaveTooltipBgClean}`,
          tooltipTextClass: "text-green-600",
        };
      } else if (parts.work.toLowerCase() === "hd") {
        return {
          label: `Half Day ${leaveInfo.label} (No Check-in)`,
          gridClass: `bg-gradient-to-r from-orange-100 to-${leaveBgClean} ${leaveInfo.gridClass.match(/text-\S+/)?.[0] || "text-orange-800"}`,
          tooltipBgClass: `bg-gradient-to-r from-orange-50 to-${leaveTooltipBgClean}`,
          tooltipTextClass: "text-orange-600",
        };
      }
    }
  }

  // 2. Try to match leaveTypes from database config
  if (leaveTypes && leaveTypes.length > 0) {
    const matched = leaveTypes.find(lt => {
      const fc = lt.filterCode.toLowerCase();
      const displayName = lt.displayName;
      const shortCode = getShortCode(displayName, lt.filterCode);
      const backendSlice = fc.toUpperCase().slice(0, 3);
      const nameSlice = displayName.toUpperCase().slice(0, 3);

      const s = lowerStatus.toUpperCase();
      return (
        fc.toUpperCase() === s ||
        shortCode.toUpperCase() === s ||
        backendSlice === s ||
        nameSlice === s
      );
    });
    if (matched) {
      const shortCode = getShortCode(matched.displayName, matched.filterCode);
      return getStandardOrCustomLeaveInfo(shortCode, matched.displayName);
    }
  }

  // 3. Fallback for standard leaves (both by abbreviation and by filter code name)
  const standardLeaveNames: Record<string, { label: string; code: string }> = {
    earned: { label: "Earned Leave", code: "el" },
    earned_leave: { label: "Earned Leave", code: "el" },
    el: { label: "Earned Leave", code: "el" },
    sick: { label: "Sick Leave", code: "sl" },
    sick_leave: { label: "Sick Leave", code: "sl" },
    sl: { label: "Sick Leave", code: "sl" },
    lwp: { label: "Leave Without Pay", code: "lwp" },
    leave_without_pay: { label: "Leave Without Pay", code: "lwp" },
    wfh: { label: "Work From Home", code: "wfh" },
    work_from_home: { label: "Work From Home", code: "wfh" },
    flexi_weekend: { label: "Flexi Weekend Leave", code: "fwl" },
    flexi_weekend_leave: { label: "Flexi Weekend Leave", code: "fwl" },
    fwl: { label: "Flexi Weekend Leave", code: "fwl" },
    compensatory: { label: "Compensatory Leave", code: "cpl" },
    compensatory_leave: { label: "Compensatory Leave", code: "cpl" },
    cpl: { label: "Compensatory Leave", code: "cpl" },
    optional_holiday: { label: "Optional Holiday", code: "oh" },
    oh: { label: "Optional Holiday", code: "oh" },
  };

  const std = standardLeaveNames[lowerStatus];
  if (std) {
    return getStandardOrCustomLeaveInfo(std.code, std.label);
  }

  // 4. General Attendance Statuses
  switch (lowerStatus) {
    case "p":
    case "present":
      return {
        label: "Present",
        gridClass: "bg-green-100 text-green-800",
        tooltipBgClass: "bg-green-50",
        tooltipTextClass: "text-green-600",
      };
    case "a":
    case "absent":
      return {
        label: "Absent",
        gridClass: "bg-red-100 text-red-800",
        tooltipBgClass: "bg-red-50",
        tooltipTextClass: "text-red-600",
      };
    case "hd":
    case "half_day":
      return {
        label: "Half Day",
        gridClass: "bg-orange-100 text-orange-800",
        tooltipBgClass: "bg-orange-50",
        tooltipTextClass: "text-orange-600",
      };
    case "hl":
    case "holiday":
      return {
        label: "Holiday",
        gridClass: "bg-orange-100 text-orange-800",
        tooltipBgClass: "bg-orange-50",
        tooltipTextClass: "text-orange-600",
      };
    case "late":
      return {
        label: "Late",
        gridClass: "bg-yellow-100 text-yellow-800",
        tooltipBgClass: "bg-yellow-50",
        tooltipTextClass: "text-yellow-600",
      };
    case "nm":
      return {
        label: "Not Marked",
        gridClass: "bg-slate-100 text-slate-600",
        tooltipBgClass: "bg-slate-50",
        tooltipTextClass: "text-slate-500",
      };
    default:
      // Treat unknown code as a custom abbreviation (e.g. "RAI" or "PAT" or "MAT")
      return getStandardOrCustomLeaveInfo(status, status.toUpperCase());
  }
};

export const getStatusDescription = (
  status: string,
  leaveTypes?: IMasterConfigOption[]
): string => {
  return getStatusDisplayInfo(status, leaveTypes).label;
};

export const getStatusColor = (
  status: string,
  leaveTypes?: IMasterConfigOption[]
): string => {
  return getStatusDisplayInfo(status, leaveTypes).gridClass;
};

export const getStatusTextColor = (
  status: string,
  leaveTypes?: IMasterConfigOption[]
): string => {
  const statusColor = getStatusColor(status, leaveTypes);
  const textColorMatch = statusColor.match(/text-\S+/);
  return textColorMatch ? textColorMatch[0] : "text-slate-800";
};

/**
 * Format date string (YYYY-MM-DD) to localized date
 * Uses local date parsing to avoid timezone issues
 * @deprecated Use formatLocalDate from timeUtils instead
 */
export const formatDate = (date: string): string => {
  // Parse date string as local date to avoid timezone issues
  const [year, month, day] = date.split("-").map((num) => parseInt(num, 10));
  const localDate = new Date(year, month - 1, day);

  return localDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const getMonthName = (month: number): string => {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return months[month - 1];
};

export const getDaysInMonth = (year: number, month: number): number => {
  return new Date(year, month, 0).getDate();
};

// Helper function to check if a date is a Sunday
export const isSunday = (year: number, month: number, day: number): boolean => {
  const date = new Date(year, month - 1, day);
  const dayOfWeek = date.getDay();
  return dayOfWeek === 0; // Sunday = 0
};

// Helper function to get day name for Sunday
export const getSundayDayName = (
  year: number,
  month: number,
  day: number
): string => {
  return "Sunday";
};

// Helper function to determine attendance status based on work hours and leave status
// Status is based on netWorkHours (totalWorkHours - breaks), not totalWorkHours
const determineAttendanceStatus = (
  record: AttendanceRecord,
  hasLeaveForDay: boolean,
  isHalfDayLeave: boolean = false,
  fullDayHours: number = 7,
  halfDayHours: number = 4
): string => {
  const workHours = record.netWorkHours ?? record.totalWorkHours ?? 0;

  // If employee has a half-day leave for this day, always show as half-day
  if (isHalfDayLeave) {
    return "HD"; // Half-day
  }

  // If employee has full-day leave for this day, show leave status
  if (hasLeaveForDay) {
    return getStatusLabel(record.status);
  }

  // Work hours based status determination:
  // < halfDayHours hours = Absent
  // >= halfDayHours hours AND < fullDayHours hours = Half Day
  // >= fullDayHours hours = Present
  if (workHours < halfDayHours) {
    return "A"; // Absent
  } else if (workHours >= halfDayHours && workHours < fullDayHours) {
    return "HD"; // Half Day
  }

  // >= fullDayHours hours - use original status (Present)
  return getStatusLabel(record.status);
};

export const generateAttendanceTable = (
  attendanceRecords: AttendanceRecord[],
  leaveRequests: LeaveRequest[],
  filters: AttendanceFilters,
  employees: Employee[] = [],
  fullDayHours: number = 7,
  halfDayHours: number = 4
): { [key: string]: { [key: string]: string } } => {
  const daysInMonth = getDaysInMonth(filters.year, filters.month);

  // Group attendance records by employee
  const employeeAttendance: { [key: string]: { [key: string]: string } } = {};

  // First, collect all approved leave requests for each employee and day
  const employeeLeaveDays: { [key: string]: { [key: string]: string } } = {};
  // Track half-day leaves separately
  const employeeHalfDayLeaves: { [key: string]: Set<string> } = {};

  leaveRequests.forEach((request) => {
    if (request.status !== "approved" && request.status !== "approved_admin")
      return;

    // For admin view, find employee by matching userId
    let employeeId = request.userId;
    if (employees.length > 0) {
      // Try to find employee by matching userId
      const employee = employees.find((emp) => emp._id === request.userId);
      if (!employee) return;
      employeeId = employee._id;
    } else {
      // For user view, use the request.userId directly since we don't have employees list
      // But we need to match it with the attendance records userId format
      // Attendance records have userId as an object with _id, so we need to match against that
      const attendanceRecord = attendanceRecords.find(
        (record) => record.userId?._id === request.userId
      );
      if (attendanceRecord && attendanceRecord.userId) {
        employeeId = attendanceRecord.userId._id;
      }
    }

    // Parse dates and normalize to compare only date part (ignore time)
    const requestStartDate = new Date(request.startDate);
    requestStartDate.setHours(0, 0, 0, 0); // Normalize to start of day
    const requestEndDate = new Date(request.endDate);
    requestEndDate.setHours(23, 59, 59, 999); // Normalize to end of day

    // Check if leave request overlaps with the selected month
    const monthStart = new Date(filters.year, filters.month - 1, 1);
    monthStart.setHours(0, 0, 0, 0); // Normalize to start of day
    const monthEnd = new Date(filters.year, filters.month, 0);
    monthEnd.setHours(23, 59, 59, 999); // Normalize to end of day

    if (requestStartDate <= monthEnd && requestEndDate >= monthStart) {
      if (!employeeLeaveDays[employeeId]) {
        employeeLeaveDays[employeeId] = {};
      }
      if (!employeeHalfDayLeaves[employeeId]) {
        employeeHalfDayLeaves[employeeId] = new Set<string>();
      }

      // Calculate overlapping days (dates are already normalized)
      const overlapStart = new Date(
        Math.max(requestStartDate.getTime(), monthStart.getTime())
      );
      overlapStart.setHours(0, 0, 0, 0); // Ensure normalized
      const overlapEnd = new Date(
        Math.min(requestEndDate.getTime(), monthEnd.getTime())
      );
      overlapEnd.setHours(23, 59, 59, 999); // Ensure normalized

      for (
        let d = new Date(overlapStart);
        d <= overlapEnd;
        d.setDate(d.getDate() + 1)
      ) {
        const dayOfMonth = d.getDate().toString();
        employeeLeaveDays[employeeId][dayOfMonth] = (
          request.leaveType
        );

        // Track half-day leaves
        if (request.isHalfDay) {
          employeeHalfDayLeaves[employeeId].add(dayOfMonth);
        }
      }
    }
  });

  // Now process attendance records with the 4-hour rule
  attendanceRecords.forEach((record) => {
    const employeeId = record.userId?._id;
    if (!employeeId) return;
    const date = new Date(record.date).getDate().toString();

    if (!employeeAttendance[employeeId]) {
      employeeAttendance[employeeId] = {};
    }

    // Check if employee has leave for this day
    const hasLeaveForDay =
      employeeLeaveDays[employeeId] && employeeLeaveDays[employeeId][date];
    // Check if employee has half-day leave for this day
    const isHalfDayLeave =
      employeeHalfDayLeaves[employeeId] &&
      employeeHalfDayLeaves[employeeId].has(date);

    // Determine the correct status based on work hours and leave status
    const status = determineAttendanceStatus(
      record,
      !!hasLeaveForDay,
      isHalfDayLeave,
      fullDayHours,
      halfDayHours
    );
    employeeAttendance[employeeId][date] = status;
  });

  // Finally, add leave days where there are no attendance records
  Object.keys(employeeLeaveDays).forEach((employeeId) => {
    if (!employeeAttendance[employeeId]) {
      employeeAttendance[employeeId] = {};
    }

    Object.keys(employeeLeaveDays[employeeId]).forEach((day) => {
      // Only add if no attendance record exists for this day
      if (!employeeAttendance[employeeId][day]) {
        employeeAttendance[employeeId][day] =
          employeeLeaveDays[employeeId][day];
      }
    });
  });

  return employeeAttendance;
};

export const calculateEmployeeSummary = (
  employeeId: string,
  attendanceRecords: AttendanceRecord[],
  leaveRequests: LeaveRequest[],
  filters: AttendanceFilters,
  fullDayHours: number = 7,
  halfDayHours: number = 4
): AttendanceSummary => {
  const employeeRecords = attendanceRecords.filter(
    (record) => record.userId?._id === employeeId
  );

  // Get approved leave requests for this employee in the selected month
  const startDate = new Date(filters.year, filters.month - 1, 1);
  startDate.setHours(0, 0, 0, 0); // Normalize to start of day
  const endDate = new Date(filters.year, filters.month, 0);
  endDate.setHours(23, 59, 59, 999); // Normalize to end of day

  const employeeLeaveRequests = leaveRequests.filter((request) => {
    // Check if the IDs match - convert both to strings for comparison
    const requestUserId = String(request.userId || "");
    const employeeIdStr = String(employeeId || "");
    if (requestUserId !== employeeIdStr) {
      return false;
    }
    if (request.status !== "approved" && request.status !== "approved_admin") {
      return false;
    }

    // Parse dates and normalize to compare only date part (ignore time)
    const requestStartDate = new Date(request.startDate);
    requestStartDate.setHours(0, 0, 0, 0); // Normalize to start of day
    const requestEndDate = new Date(request.endDate);
    requestEndDate.setHours(23, 59, 59, 999); // Normalize to end of day

    // Check if dates are valid
    if (isNaN(requestStartDate.getTime()) || isNaN(requestEndDate.getTime())) {
      return false;
    }

    // Check if leave request overlaps with the selected month
    // Overlap occurs if: requestStartDate <= endDate AND requestEndDate >= startDate
    const overlaps = requestStartDate <= endDate && requestEndDate >= startDate;
    return overlaps;
  });

  // Calculate leave days from approved requests
  let totalLeaveDays = 0;
  const leaveDaysSet = new Set<string>(); // Track specific leave days
  const halfDayLeaveDaysSet = new Set<string>(); // Track half-day leave days

  employeeLeaveRequests.forEach((request) => {
    // Parse dates and normalize to compare only date part (ignore time)
    const requestStartDate = new Date(request.startDate);
    requestStartDate.setHours(0, 0, 0, 0); // Normalize to start of day
    const requestEndDate = new Date(request.endDate);
    requestEndDate.setHours(23, 59, 59, 999); // Normalize to end of day

    // Calculate overlapping days with the selected month
    const monthStart = new Date(filters.year, filters.month - 1, 1);
    monthStart.setHours(0, 0, 0, 0); // Normalize to start of day
    const monthEnd = new Date(filters.year, filters.month, 0);
    monthEnd.setHours(23, 59, 59, 999); // Normalize to end of day

    const overlapStart = new Date(
      Math.max(requestStartDate.getTime(), monthStart.getTime())
    );
    const overlapEnd = new Date(
      Math.min(requestEndDate.getTime(), monthEnd.getTime())
    );

    if (overlapStart <= overlapEnd) {
      const daysDiff =
        Math.ceil(
          (overlapEnd.getTime() - overlapStart.getTime()) /
            (1000 * 60 * 60 * 24)
        ) + 1;

      if (request.isHalfDay) {
        totalLeaveDays += daysDiff * 0.5;
      } else {
        totalLeaveDays += daysDiff;
      }

      // Track specific leave days
      for (
        let d = new Date(overlapStart);
        d <= overlapEnd;
        d.setDate(d.getDate() + 1)
      ) {
        const dateStr = d.toISOString().split("T")[0];
        leaveDaysSet.add(dateStr);
        if (request.isHalfDay) {
          halfDayLeaveDaysSet.add(dateStr);
        }
      }
    }
  });

  // Apply work hours rule when calculating present, absent, and half days
  // < halfDayHours hours = Absent
  // >= halfDayHours hours AND < fullDayHours hours = Half Day
  // >= fullDayHours hours = Present
  let presentDays = 0;
  let absentDays = 0;
  let halfDays = 0;

  employeeRecords.forEach((record) => {
    const recordDate = new Date(record.date).toISOString().split("T")[0];
    const hasLeaveForDay = leaveDaysSet.has(recordDate);
    const isHalfDayLeave = halfDayLeaveDaysSet.has(recordDate);
    const workHours = record.totalWorkHours || 0;

    // If there's a half-day leave for this date, count as half-day regardless of work hours
    if (isHalfDayLeave) {
      halfDays++;
    } else if (hasLeaveForDay) {
      // If full-day leave is applied, count as leave (not absent)
      // Leave days are already counted in totalLeaveDays
    } else if (workHours < halfDayHours) {
      // Less than halfDayHours = absent
      absentDays++;
    } else if (workHours >= halfDayHours && workHours < fullDayHours) {
      // halfDayHours to fullDayHours = half day
      halfDays++;
    } else if (workHours >= fullDayHours) {
      // fullDayHours+ hours = present
      presentDays++;
    } else if (record.status === "absent") {
      absentDays++;
    }
  });

  // Calculate total absent days including half days (0.5 each)
  const totalAbsentDays = absentDays + halfDays * 0.5;

  // Calculate work hours from attendance records
  let totalWorkHours = 0;
  let totalBreakHours = 0;
  let netWorkHours = 0;

  employeeRecords.forEach((record) => {
    if (record.totalWorkHours) {
      totalWorkHours += record.totalWorkHours;
    }
    if (record.totalBreakHours) {
      totalBreakHours += record.totalBreakHours;
    }
    if (record.netWorkHours) {
      netWorkHours += record.netWorkHours;
    }
  });

  // If netWorkHours is not calculated, calculate it as totalWorkHours - totalBreakHours
  if (netWorkHours === 0 && totalWorkHours > 0) {
    netWorkHours = totalWorkHours - totalBreakHours;
  }

  // Ensure totalBreakHours is always a number (0 if no breaks taken)
  totalBreakHours = totalBreakHours || 0;

  const summary = {
    totalDays: getDaysInMonth(filters.year, filters.month),
    presentDays: presentDays,
    absentDays: totalAbsentDays,
    halfDays: halfDays,
    leaveDays: totalLeaveDays,
    totalLeaves: totalLeaveDays,
    totalWorkHours: Math.round(totalWorkHours * 100) / 100, // Round to 2 decimal places
    totalBreakHours: Math.round(totalBreakHours * 100) / 100, // Round to 2 decimal places
    netWorkHours: Math.round(netWorkHours * 100) / 100, // Round to 2 decimal places
  };

  return summary;
};
